import { forwardRef, useState } from 'react';
import { CurrencyInput, CurrencyInputProps } from './CurrencyInput';
import { useTranslation } from 'react-i18next';
import { useDeepCompareEffect } from 'react-use';
import { RecurringFrequency } from '../../../../generated/graphql';
import { FormRecurringMonetaryAmountValue } from '../types';
import { InputProps } from './Input';
import { InputContainer, InputInlineToggle } from './components';
import { elementsInputClasses } from './util';

export interface RecurringMonetaryInputProps extends Omit<InputProps, 'inputProps' | 'onChange'> {
  registerOnChangeOnEmptyValue?: boolean;
  value?: FormRecurringMonetaryAmountValue | null;
  onChange?: (p: FormRecurringMonetaryAmountValue | null) => void;
  onFrequencyChange?: (frequency: RecurringFrequency | null) => void;
  inputProps?: Omit<
    CurrencyInputProps,
    | 'onClick'
    | 'name'
    | 'disabled'
    | 'onBlur'
    | 'id'
    | 'trailing'
    | 'ref'
    | 'prefix'
    | 'onValueChange'
    | 'className'
    | 'value'
    | 'decimalsLimit'
  >;
}

export const RecurringMonetaryInput = forwardRef<HTMLInputElement, RecurringMonetaryInputProps>(
  (
    {
      name,
      value,
      onBlur,
      onChange,
      onFrequencyChange,
      inputProps,
      registerOnChangeOnEmptyValue,
      ...inputContainerProps
    },
    ref
  ) => {
    const { t: tForm } = useTranslation('form');
    const [recurringAmount, setRecurringAmount] = useState<FormRecurringMonetaryAmountValue>({
      frequency: RecurringFrequency.Annually,
      amount: null,
    });

    useDeepCompareEffect(() => {
      if (value) setRecurringAmount(value);
    }, [value]);

    const notifyOnChange = (newValue: FormRecurringMonetaryAmountValue) => {
      if (registerOnChangeOnEmptyValue) {
        onChange?.(newValue);
      } else {
        onChange?.(newValue.amount === null ? null : newValue);
      }
    };

    return (
      <InputContainer
        {...inputContainerProps}
        isReadOnly={!!inputProps?.readOnly}
        trailing={
          <InputInlineToggle
            onChange={(index) => {
              const newFrequency = index === 0 ? RecurringFrequency.Monthly : RecurringFrequency.Annually;
              const newValue = { ...recurringAmount, frequency: newFrequency };
              setRecurringAmount(newValue);
              onFrequencyChange?.(newFrequency);
              notifyOnChange(newValue);
            }}
            selected={recurringAmount.frequency === RecurringFrequency.Monthly ? 0 : 1}
            items={[
              tForm('general-frequency-input-frequency-month-abbr'),
              tForm('general-frequency-input-frequency-year-abbr'),
            ]}
          />
        }
      >
        <CurrencyInput
          ref={ref}
          id={name}
          prefix="$"
          name={name}
          onBlur={onBlur}
          decimalsLimit={2}
          className={elementsInputClasses.input}
          value={recurringAmount.amount ?? undefined}
          onValueChange={(numberString) => {
            const number = Number(numberString);
            const newValue = {
              amount: isNaN(number) ? null : number,
              frequency: recurringAmount.frequency ?? RecurringFrequency.Annually,
            };
            setRecurringAmount(newValue);
            notifyOnChange(newValue);
          }}
          {...inputProps}
        />
      </InputContainer>
    );
  }
);
